import api from "../api";
import { decryptChatText, encryptChatText, isEncryptedChatPayload } from "../utils/chatMessageCrypto";

let cached = {
  ts: 0,
  settings: null,
  e2eKeyBase64: null,
};

const CACHE_TTL_MS = 10_000;
let loadInFlight = null;

function getEnterpriseIdFromStorage() {
  const raw =
    sessionStorage.getItem("enterprise_id") ||
    localStorage.getItem("enterprise_id");
  const direct = Number.parseInt(String(raw || ""), 10);
  if (Number.isFinite(direct) && direct > 0) return direct;

  // Fallback: many parts of the app persist the logged-in user object but not enterprise_id.
  // Try to infer enterprise from that object so E2E decryption works on session history refresh.
  try {
    const userRaw = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (!userRaw) return null;
    const user = JSON.parse(userRaw);
    const fromUser =
      user?.enterprise_id ?? user?.enterprise_fid ?? user?.enterpriseId ?? null;
    const inferred = Number.parseInt(String(fromUser || ""), 10);
    return Number.isFinite(inferred) && inferred > 0 ? inferred : null;
  } catch {
    return null;
  }
}

export async function loadChatSecurityContext({ force = false } = {}) {
  const now = Date.now();
  if (!force && cached.settings && now - cached.ts < CACHE_TTL_MS) return cached;
  if (!force && loadInFlight) return loadInFlight;

  const promise = (async () => {
    const enterpriseId = getEnterpriseIdFromStorage();
    if (!enterpriseId) {
      cached = { ts: Date.now(), settings: { chat_encryption_mode: "none" }, e2eKeyBase64: null };
      return cached;
    }

    let settings = null;
    try {
      settings = await api
        .get(`/enterprise-chat-security-settings/byEnterprise/${enterpriseId}`, {
          skipLoader: true,
          silent: true,
        })
        .then((r) => r.data);
    } catch {
      // If we can't load settings (e.g. transient network / auth), don't crash the chat UI.
      // Callers that require E2E decryption can retry with `force: true`.
      cached = { ts: Date.now(), settings: { chat_encryption_mode: "none" }, e2eKeyBase64: null };
      return cached;
    }

    const mode = String(settings?.chat_encryption_mode || "none").toLowerCase();

    let e2eKeyBase64 = null;
    if (mode === "end_to_end") {
      try {
        const keyRes = await api.get("/enterprise-chat-security-settings/e2e-key", {
          skipLoader: true,
          silent: true,
        });
        e2eKeyBase64 = keyRes?.data?.key_base64 || null;
      } catch {
        e2eKeyBase64 = null;
      }
    }

    cached = { ts: Date.now(), settings, e2eKeyBase64 };
    return cached;
  })();

  if (!force) loadInFlight = promise;
  try {
    return await promise;
  } finally {
    if (!force && loadInFlight === promise) loadInFlight = null;
  }
}

export async function encryptOutgoingChatText(plaintext) {
  const ctx = await loadChatSecurityContext();
  const mode = String(ctx?.settings?.chat_encryption_mode || "none").toLowerCase();
  if (mode !== "end_to_end") return String(plaintext ?? "");
  if (!ctx?.e2eKeyBase64) return String(plaintext ?? "");
  return encryptChatText({ keyBase64: ctx.e2eKeyBase64, plaintext });
}

export async function decryptIncomingChatText(maybeEncrypted, { modeOverride = null } = {}) {
  // Single ctx load (also single-flight deduped inside loadChatSecurityContext).
  const ctx = await loadChatSecurityContext();
  const mode = modeOverride
    ? String(modeOverride).toLowerCase()
    : String(ctx?.settings?.chat_encryption_mode || "none").toLowerCase();

  if (mode !== "end_to_end") return String(maybeEncrypted ?? "");
  if (!isEncryptedChatPayload(maybeEncrypted)) return String(maybeEncrypted ?? "");

  if (!ctx?.e2eKeyBase64) return String(maybeEncrypted ?? "");

  try {
    return await decryptChatText({ keyBase64: ctx.e2eKeyBase64, encoded: maybeEncrypted });
  } catch {
    return String(maybeEncrypted ?? "");
  }
}
